/**
 * Minimal React data-fetching hooks over {@link ApiClient}.
 *
 * Deliberately dependency-free (no react-query) to keep the shared package light;
 * apps may still adopt a heavier cache layer on top. ``useApiQuery`` covers reads,
 * ``useApiMutation`` covers writes (create order, transition, upload proof, …).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, apiClient, type ApiClient } from "./client";

export interface QueryState<T> {
  readonly data: T | null;
  readonly error: ApiError | null;
  readonly isLoading: boolean;
  /** Manually re-run the query. */
  readonly refetch: () => void;
}

export interface UseApiQueryOptions<T> {
  /** Skip fetching until ``true`` (e.g. waiting for an id). Defaults to ``true``. */
  readonly enabled?: boolean;
  /** Client instance to use. Defaults to the shared {@link apiClient}. */
  readonly client?: ApiClient;
  /** Re-fetch whenever any value here changes. */
  readonly deps?: ReadonlyArray<unknown>;
  /** Initial data before the first fetch resolves. */
  readonly initialData?: T;
}

/**
 * Fetch ``GET path`` and expose ``{ data, error, isLoading, refetch }``.
 */
export function useApiQuery<T>(
  path: string,
  options: UseApiQueryOptions<T> = {},
): QueryState<T> {
  const {
    enabled = true,
    client = apiClient,
    deps = [],
    initialData,
  } = options;

  const [data, setData] = useState<T | null>(initialData ?? null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    client
      .get<T>(path, { signal: controller.signal })
      .then((result) => {
        setData(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(
          err instanceof ApiError ? err : new ApiError(0, "Network error", err),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, client, nonce, ...deps]);

  return { data, error, isLoading, refetch };
}

export interface MutationState<TResult, TVariables> {
  readonly data: TResult | null;
  readonly error: ApiError | null;
  readonly isLoading: boolean;
  /** Trigger the mutation; resolves with the result or rejects with {@link ApiError}. */
  readonly mutate: (variables: TVariables) => Promise<TResult>;
  /** Reset state back to idle. */
  readonly reset: () => void;
}

/**
 * Wrap a write operation (``mutationFn``) with loading/error state.
 *
 * @example
 *   const { mutate, isLoading } = useApiMutation(
 *     (vars: CreateOrder) =>
 *       apiClient.post<Order>("/orders/", { body: vars, idempotencyKey: vars.key }),
 *   );
 */
export function useApiMutation<TResult, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
): MutationState<TResult, TVariables> {
  const [data, setData] = useState<TResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fnRef = useRef(mutationFn);
  fnRef.current = mutationFn;

  const mutate = useCallback(async (variables: TVariables): Promise<TResult> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fnRef.current(variables);
      if (mountedRef.current) {
        setData(result);
      }
      return result;
    } catch (err: unknown) {
      const apiError =
        err instanceof ApiError ? err : new ApiError(0, "Network error", err);
      if (mountedRef.current) {
        setError(apiError);
      }
      throw apiError;
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, error, isLoading, mutate, reset };
}
